<!-- 
Listing of provider contacts.
-->
<template>
  <div>
    <v-card flat>
      <our-simple-table-header
        :total-items="listing.paging.totalItems"
        :expand-multiple="expandMultiple"
        has-expanders
        @click:refresh="refresh"
        @click:expandAll="expandAll"
        @click:collapseAll="collapseAll"
        @click:toggleMultiple="toggleMultiple"
      />
      <our-table-busy-indicator :show="listing.loading"/>
      <v-data-table 
        :headers="headers" 
        :items="listing.data" 
        :pagination="listing.paging" 
        :total-items="listing.paging.totalItems" 
        :no-data-text="noDataText"
        must-sort 
        hide-actions        
        class="table-expandable" 
        @update:pagination="updatePagination"
      >
        <template slot="items" slot-scope="props">
          <tr @click="expandItem(props.index)">
            <our-nav-col :row="props.item.type"/>
            <td>
              <provider-contact-sync-status-indicators :contact="props.item" />
            </td>
            <td>
              <provider-contact-status-indicators :contact="props.item" :levy-collection-type="levyCollectionType" />
            </td>
            <td>{{ props.item.type }}</td>
            <td>{{ props.item.name }}</td>
            <td>{{ props.item.email }}</td>
            <td class="expand-icon">
              <v-icon :class="['body-1 ' + (listing.expanded[props.index] ? 'expanded' : '')]">keyboard_arrow_down</v-icon>
            </td>
          </tr>
          <our-expander-row :colspan="6" :expanded="listing.expanded[props.index]">
            <v-card v-if="listing.expanded[props.index]" flat class="grey lighten-5">
              <v-card-text class="pb-2">
                <dl>
                  <dt>Type:</dt>
                  <dd>{{ getTypeDescription(props.item.type) | notSpecified }}</dd>
                  <dt>Sector:</dt>
                  <dd>{{ lookups.providerContactSectorDescriptions[props.item.sector] | notSpecified }}</dd>
                  <dt>Role:</dt>
                  <dd>{{ props.item.role | notSpecified }}</dd>
                  <dt>Position:</dt>
                  <dd>{{ lookups.contactPositionDescriptions[props.item.position] | notSpecified }}</dd>
                  <template v-if="props.item.phone">
                    <dt>Phone:</dt>
                    <dd>{{ props.item.phone }}</dd>
                  </template>
                  <template v-if="props.item.phoneDirect">
                    <dt>Phone (direct):</dt>
                    <dd>{{ props.item.phoneDirect }}</dd>
                  </template>
                  <template v-if="props.item.phoneMobile">
                    <dt>Phone (mobile):</dt>
                    <dd>{{ props.item.phoneMobile }}</dd>
                  </template>
                  <template v-if="props.item.phoneInternational">
                    <dt>Phone (international):</dt>
                    <dd>{{ props.item.phoneInternational }}</dd>
                  </template>
                  <template v-for="(item, index) in props.item.postalAddresses">
                    <dt :key="`${index}-t`">Postal address:</dt>
                    <dd :key="`${index}-d`">{{ item }}</dd>
                  </template>
                  <dt>Created:</dt>
                  <dd>{{ props.item.created | compactDateTime }}</dd>
                </dl>
              </v-card-text>
              <v-card-actions v-if="canPromotePrimaryContact">
                <provider-contact-promote-primary-buttons :contact="props.item" :levy-collection-type="levyCollectionType" />
              </v-card-actions>
              <v-card-actions>
                <provider-contact-change-status-buttons v-if="props.item.synchroniseWithHits == false && canEditNonSyncedContactStatus" :contact="props.item" :levy-collection-type="levyCollectionType" />
              </v-card-actions>
            </v-card>
          </our-expander-row>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import * as claimTypes from "@/store/claim-types";
import tableMixins from "@/mixins/table.mixins";
import filterMixins from "@/mixins/filter.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import ProviderContactPromotePrimaryButtons from "@/components/provider-contacts/ProviderContactPromotePrimaryButtons.vue";
import ProviderContactChangeStatusButtons from "@/components/provider-contacts/ProviderContactChangeStatusButtons.vue";
import ProviderContactStatusIndicators from "@/components/provider-contacts/ProviderContactStatusIndicators.vue";
import ProviderContactSyncStatusIndicators from "@/components/provider-contacts/ProviderContactSyncStatusIndicators.vue";
import * as action from "@/store/provider-contacts/action-types";
import * as mutation from "@/store/provider-contacts/mutation-types";

export default {
  components: {
    ProviderContactPromotePrimaryButtons,
    ProviderContactChangeStatusButtons,
    ProviderContactStatusIndicators,
    ProviderContactSyncStatusIndicators
  },
  mixins: [tableMixins, filterMixins, lookupMixins],
  props: {
    providerId: {
      type: Number,
      default: null
    },
    levyCollectionType: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      action: action.LIST,
      pagingMutation: mutation.LIST_SET_PAGING,
      expandMutation: mutation.LIST_TOGGLE_EXPAND,
      defaultNoDataText: "This provider does not have any contacts yet."
    };
  },
  computed: {
    headers() {
      return [
        { text: "", value: "Id", class: "navCol", width: 0, sortable: false },
        { text: "Sync", value: "SynchroniseWithHits,VslStatus,HelpStatus,Surname,FirstName", width: 2, sortable: true },
        { text: "Status", value: "VslStatus,HelpStatus,Surname,FirstName", sortable: true },
        { text: "Type", value: "Type", sortable: true },
        { text: "Name", value: "Surname,FirstName", sortable: true },
        { text: "Email", value: "Email", sortable: true },
        { text: "", value: "Detail", width: 5, sortable: false }
      ];
    },
    listing() {
      return this.$store.state.providerContacts.listing;
    },
    loading() {
      return this.listing.loading;
    },
    loaded() {
      return this.listing.loaded;
    },
    canEditNonSyncedContactStatus() {
      return this.$store.getters.claims.includes(claimTypes.PROVIDER_NOTSYNCED_CONTACT_STATUS_EDIT);
    },
    canPromotePrimaryContact() {
      return this.$store.getters.claims.includes(claimTypes.PROVIDER_PROMOTED_PRIMARY_CONTACTS_EDIT);
    }
  },
  watch: {
    providerId() {
      this.reload();
    },
    loaded() {
      this.reload();
    }
  },
  mounted() {
    this.reload();
  },
  methods: {
    reload() {
      const needsReloaded = (!this.loaded && !this.loading) || this.providerId != this.listing.providerId;
      if (needsReloaded) {
        this.$store.commit(mutation.LIST_SET_FILTERS, { providerId: this.providerId });
        this.populate();
      }
    },
    getTypeDescription(type) {
      return type ? `${type} (${this.lookups.contactTypeDescriptions[type]})` : "";
    }
  }
};
</script>
